import React, { useMemo } from "react";

type MascotMood = "happy" | "wave" | "cheer" | "curious" | "sleep" | "sad";

type Props = {
  size?: number;
  color?: string;
  mood?: MascotMood;
  sparkles?: boolean;
  bob?: boolean;
};

// Builds the same SVG as src/components/Mascot.tsx but inlined so the
// web shell stays plain React (no react-native-web).
function buildSvg({
  color,
  mood,
  sparkles,
}: Required<Pick<Props, "color" | "mood">> & { sparkles?: boolean }): string {
  const ink = "#2E2316";
  const cheek = "rgba(244,120,90,0.35)";

  const armLeft = `<ellipse cx="34" cy="118" rx="14" ry="20" fill="${color}"${
    mood === "cheer" ? ' transform="rotate(-30 34 118)"' : ""
  }/>`;
  const armRight = `<ellipse cx="166" cy="118" rx="14" ry="20" fill="${color}"${
    mood === "cheer" || mood === "wave" ? ' transform="rotate(30 166 118)"' : ""
  }/>`;
  const body = `<path d="M40 92 C 40 50, 70 28, 100 28 C 130 28, 160 50, 160 92 C 160 142, 134 168, 100 168 C 66 168, 40 142, 40 92 Z" fill="${color}"/>`;
  const belly = `<ellipse cx="100" cy="118" rx="42" ry="34" fill="#fff" opacity="0.18"/>`;
  const cheeks = `<ellipse cx="70" cy="115" rx="9" ry="5" fill="${cheek}"/><ellipse cx="130" cy="115" rx="9" ry="5" fill="${cheek}"/>`;

  let eyes = "";
  if (mood === "sleep") {
    eyes =
      `<path d="M70 92 q8 7 16 0" stroke="${ink}" stroke-width="4" stroke-linecap="round" fill="none"/>` +
      `<path d="M114 92 q8 7 16 0" stroke="${ink}" stroke-width="4" stroke-linecap="round" fill="none"/>`;
  } else if (mood === "sad") {
    eyes =
      `<circle cx="78" cy="93" r="6" fill="${ink}"/>` +
      `<circle cx="122" cy="93" r="6" fill="${ink}"/>`;
  } else {
    eyes =
      `<ellipse cx="78" cy="92" rx="7" ry="9" fill="${ink}"/>` +
      `<ellipse cx="122" cy="92" rx="7" ry="9" fill="${ink}"/>` +
      `<circle cx="80" cy="89" r="2.4" fill="#fff"/>` +
      `<circle cx="124" cy="89" r="2.4" fill="#fff"/>`;
  }

  let mouth = "";
  if (mood === "sleep") {
    mouth = `<path d="M92 122 q8 4 16 0" stroke="${ink}" stroke-width="3.5" stroke-linecap="round" fill="none"/>`;
  } else if (mood === "sad") {
    mouth = `<path d="M84 130 q16 -10 32 0" stroke="${ink}" stroke-width="4" stroke-linecap="round" fill="none"/>`;
  } else if (mood === "cheer") {
    mouth = `<path d="M82 116 q18 28 36 0 q-18 8 -36 0 Z" fill="${ink}"/>`;
  } else if (mood === "curious") {
    mouth = `<ellipse cx="100" cy="124" rx="5" ry="6" fill="${ink}"/>`;
  } else {
    mouth = `<path d="M82 118 q18 22 36 0" stroke="${ink}" stroke-width="4.5" stroke-linecap="round" fill="none"/>`;
  }

  const feet =
    `<ellipse cx="80" cy="166" rx="14" ry="6" fill="${color}"/>` +
    `<ellipse cx="120" cy="166" rx="14" ry="6" fill="${color}"/>`;

  const showSparkles = sparkles || mood === "cheer";
  const sparkleSvg = showSparkles
    ? `<g fill="#FFD86B">` +
      `<path d="M30 40 l3 7 7 3 -7 3 -3 7 -3 -7 -7 -3 7 -3 z"/>` +
      `<path d="M178 60 l2 5 5 2 -5 2 -2 5 -2 -5 -5 -2 5 -2 z"/>` +
      `<path d="M16 96 l2 4 4 2 -4 2 -2 4 -2 -4 -4 -2 4 -2 z"/>` +
      `</g>`
    : "";

  const shadow = `<ellipse cx="100" cy="178" rx="58" ry="6" fill="rgba(0,0,0,0.10)"/>`;

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" width="200" height="200">` +
    shadow +
    armLeft +
    armRight +
    body +
    belly +
    cheeks +
    eyes +
    mouth +
    feet +
    sparkleSvg +
    `</svg>`
  );
}

export function WebMascot({
  size = 120,
  color = "#FF8A4C",
  mood = "happy",
  sparkles,
  bob,
}: Props) {
  const uri = useMemo(
    () =>
      `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
        buildSvg({ color, mood, sparkles }),
      )}`,
    [color, mood, sparkles],
  );
  return (
    <img
      src={uri}
      alt=""
      width={size}
      height={size}
      className={bob ? "bob" : undefined}
      style={{ display: "block" }}
    />
  );
}

export default WebMascot;
